import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Menu, MenuItem, Divider, Chip, Tooltip, Dialog } from '@mui/material';
import Iconify from '../../../../reusable-components/iconify';
import NewPurchaseDialog from './NewPurchaseDialog';
import NewRental from '../NewRental';
import { useScopeCheck } from '../../../../reusable-components/scopes';
import { useDispatch, useSelector } from '../../../../redux/store';
import { getOrderingFacilities } from '../../../../redux/slices/orderSheet';
import { setIsAddingNewTicket } from '../../../../redux/slices/tickets';
import { fetchTransportTypes, fetchUserFacilities } from '../../../../redux/slices/transports';
import { TRANSPORT_ADMIN, TRANSPORT_COORDINATOR, TRANSPORT_NURSE, TRANSPORT_VIEW_ONLY } from '../../../../components/dashboard/transports/constants';
import NewRequestForm from '../../../../components/dashboard/transports/SaveRequest/NewRequestForm';

const menuItemSx = { fontSize: 14, py: 0.75, mx: 0.5, borderRadius: '5px' };

const betaChip = <Chip label="Beta" size="small" sx={{ ml: 1, height: 18, fontSize: 11, bgcolor: 'info.active', color: 'info.main' }} />;

export default function NewMenu() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: { facilities } } = useSelector((state) => state.orderSheet);
  const transportsState = useSelector((state) => state.transports);
  const transportFacilities = transportsState?.data?.facilities ?? [];
  const transportTypes = transportsState?.data?.transportTypes ?? [];
  const canSubmitOrders = useScopeCheck(['Create-Order'], true);
  const { user } = useSelector((state) => state.user);
  // Match the Transports and Contracts header tab visibility in headerItems.js
  const isTransportsContractsSite = user?.siteId === 1 || user?.siteId === 2;
  const hasTransportsTabScope = useScopeCheck(['Super-Admin', TRANSPORT_ADMIN, TRANSPORT_COORDINATOR, TRANSPORT_NURSE, TRANSPORT_VIEW_ONLY]);
  const hasContractsTabScope = useScopeCheck(['Contracts-View', 'Super-Admin']);
  const showTransport = isTransportsContractsSite && hasTransportsTabScope;
  const showContract = isTransportsContractsSite && hasContractsTabScope;
  const canAddTransportRequest = useScopeCheck([TRANSPORT_ADMIN, TRANSPORT_COORDINATOR, TRANSPORT_NURSE], false, true);

  const [anchorEl, setAnchorEl] = useState(null);
  const [openPurchaseDialog, setOpenPurchaseDialog] = useState(false);
  const [openRentalDialog, setOpenRentalDialog] = useState(false);
  const [isAddingNewRequest, setIsAddingNewRequest] = useState(false);
  const [transportDialogSize, setTransportDialogSize] = useState('sm');

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handlePurchaseClick = () => {
    if (!facilities?.length) dispatch(getOrderingFacilities());
    setOpenPurchaseDialog(true);
    handleCloseMenu();
  };

  const handleRentalClick = () => {
    setOpenRentalDialog(true);
    handleCloseMenu();
  };

  const handleTicketClick = () => {
    dispatch(setIsAddingNewTicket(true));
    handleCloseMenu();
  };

  const handleTransportClick = () => {
    if (!transportFacilities.length) dispatch(fetchUserFacilities());
    if (!transportTypes.length) dispatch(fetchTransportTypes());
    setIsAddingNewRequest(true);
    handleCloseMenu();
  };

  const handleContractClick = () => {
    navigate('/dashboard/contracts/new?status=active');
    handleCloseMenu();
  };

  return <>
    <Button
      variant="contained"
      color="secondary"
      size="small"
      onClick={(event) => setAnchorEl(event.currentTarget)}
      endIcon={<Iconify icon="ic:baseline-arrow-drop-down" />}
      sx={{
        display: 'flex',
        padding: '4px 10px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '4px',
        borderRadius: '5px',
        boxShadow: 'none',
        '& .MuiButton-endIcon': { ml: 0 },
        '&:hover': { boxShadow: 'none' },
      }}
    >
      New
    </Button>
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleCloseMenu}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        sx: {
          width: 166,
          mt: 0.5,
          boxShadow: '0 4px 4px 0 rgba(0, 0, 0, 0.13)',
        },
      }}
    >
      <Tooltip arrow placement="left" title={canSubmitOrders ? '' : 'Order permission needed for this function'}>
        <span>
          <MenuItem onClick={handlePurchaseClick} disabled={!canSubmitOrders} sx={menuItemSx}>Purchase Order</MenuItem>
        </span>
      </Tooltip>
      <MenuItem onClick={handleRentalClick} sx={menuItemSx}>Rental</MenuItem>
      <Divider />
      <MenuItem onClick={handleTicketClick} sx={menuItemSx}>Ticket</MenuItem>
      {(showTransport || showContract) && <Divider />}
      {showTransport && (
        <Tooltip arrow placement="left" title={canAddTransportRequest ? '' : 'Transport request permission needed for this function'}>
          <span>
            <MenuItem onClick={handleTransportClick} disabled={!canAddTransportRequest} sx={menuItemSx}>Transport{betaChip}</MenuItem>
          </span>
        </Tooltip>
      )}
      {showContract && <MenuItem onClick={handleContractClick} sx={menuItemSx}>Contract{betaChip}</MenuItem>}
    </Menu>
    <NewPurchaseDialog openPurchaseDialog={openPurchaseDialog} handleClosePurchaseDialog={() => setOpenPurchaseDialog(false)} />
    {openRentalDialog && (
      <NewRental
        openRentalDialog={openRentalDialog}
        setOpenRentalDialog={setOpenRentalDialog}
        setOpenNewOrderDialog={handleCloseMenu}
      />
    )}
    <Dialog
      open={isAddingNewRequest}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        setIsAddingNewRequest(false);
      }}
      fullWidth
      maxWidth={transportDialogSize}
    >
      <NewRequestForm
        setOpen={(open) => {
          setIsAddingNewRequest(open);
          setTransportDialogSize('sm');
        }}
        facilities={transportFacilities}
        onDialogSizeChange={setTransportDialogSize}
      />
    </Dialog>
  </>;
}
